/*
 * Filename: js/core/scrambling.js
 * Purpose: Functions for scrambling the puzzle grid for the Squdle game.
 */

// Import necessary functions from other modules
import { swapCells, getRowWord, getColumnWord, countGridLetters, cloneGrid } from '../utils/gridHelpers.js';
import { shuffleArray } from '../utils/random.js';
import { getValidationState, verifyLetterBalance, hasCorrectPositions } from './validation.js'; // Import necessary validation

// No longer extending SqudleCore, export functions directly

/**
 * Scramble the grid with random swaps, ensuring no valid words, no green tiles,
 * and attempting to avoid an "all yellow" initial state.
 *
 * @param {Array<Array<string>>} grid - The grid to scramble (will be modified in place).
 * @param {Array<Array<string>>} solvedGrid - The solution grid (used for validation).
 * @param {Set<string>} validWordSet - Set of valid words for the current grid size.
 * @param {function(): number} randomFunc - A seeded random number generator function (0 to 1).
 */
export function scrambleGrid(grid, solvedGrid, validWordSet, randomFunc) {
  // Dependency checks are handled by module system, remove manual checks

  const gridSize = grid.length;
  let maxAttempts = 10; // Max attempts to break the "all yellow" state
  let allYellowCheckPassed = false;
  let attemptCycle = 0;

  console.log(`Starting grid scramble for ${gridSize}x${gridSize}...`);

  while (!allYellowCheckPassed && maxAttempts > 0) {
    attemptCycle++;
    console.log(`Scramble cycle ${attemptCycle}. Remaining attempts: ${maxAttempts}`);
    maxAttempts--; // Decrement attempt counter for this cycle

    // Step 1: Apply initial scrambling (or re-scramble if looping)
    // Use a significant number of swaps based on grid size
    const numSwaps = gridSize * gridSize * 2; // Increased swaps for potentially better initial distribution
    for (let i = 0; i < numSwaps; i++) {
      const row1 = Math.floor(randomFunc() * gridSize);
      const col1 = Math.floor(randomFunc() * gridSize);
      const row2 = Math.floor(randomFunc() * gridSize);
      const col2 = Math.floor(randomFunc() * gridSize);
      if (row1 !== row2 || col1 !== col2) {
        swapCells(grid, row1, col1, row2, col2); // Use imported function
      } else {
        i--; // Retry if the same cell was picked
      }
    }

    // Step 2: Verify/fix letter balance (crucial after swaps)
    if (!verifyLetterBalance(grid, solvedGrid)) { // Use imported function
      console.warn("Letter imbalance detected during scramble cycle! Fixing...");
      fixLetterBalance(grid, solvedGrid); // Use imported function
      // Re-verify after fix
      if (!verifyLetterBalance(grid, solvedGrid)) { // Use imported function
          console.error("CRITICAL: Failed to fix letter balance during scramble. Aborting cycle.");
          continue; // Skip to next attempt or fail
      }
    }

    // Step 3: Ensure no valid words exist in rows or columns
    ensureNoValidWords(grid, validWordSet, randomFunc); // Use private helper

    // Step 4: Ensure no letters are in their correct positions (no green tiles)
    ensureNoCorrectPositions(grid, solvedGrid, randomFunc); // Use private helper

    // --- NEW: Step 5: Check for "all yellow" condition ---
    console.log("Checking validation state after scrambling steps...");
    const validationState = getValidationState(grid, solvedGrid); // Use imported function
    let foundWhiteTile = false;

    if (validationState) {
      for (let r = 0; r < gridSize; r++) {
        // Check if validationState[r] is valid before trying to access length
        if (!validationState[r] || validationState[r].length !== gridSize) {
             console.error(`Scramble Check Error: Invalid validation state structure at row ${r}. Skipping check.`);
             foundWhiteTile = true; // Assume okay to prevent loop on error
             break;
        }
        for (let c = 0; c < gridSize; c++) {
          if (validationState[r][c] === 0) { // State 0 represents a white tile
            foundWhiteTile = true;
            break;
          }
        }
        if (foundWhiteTile) break;
      }
    } else {
      console.error("Scramble Check Error: Failed to get validation state. Assuming check passes.");
      foundWhiteTile = true; // Assume okay to prevent infinite loop on error
    }

    if (foundWhiteTile) {
      allYellowCheckPassed = true; // Condition met (at least one white tile), exit loop
      console.log("Scramble check passed: Found at least one white tile.");
    } else {
      console.warn(`Scramble Result: Grid has no white tiles (all yellow/green). Applying additional swaps...`);
      // Perform a few more random swaps specifically to break the "all yellow" state.
      // We only need to do this if the check failed.
      const extraSwaps = Math.max(5, gridSize * 2); // Add a minimum number of swaps
      console.log(`Performing ${extraSwaps} extra swaps.`);
      for (let i = 0; i < extraSwaps; i++) {
        const r1 = Math.floor(randomFunc() * gridSize);
        const c1 = Math.floor(randomFunc() * gridSize);
        const r2 = Math.floor(randomFunc() * gridSize);
        const c2 = Math.floor(randomFunc() * gridSize);
        if (r1 !== r2 || c1 !== c2) {
          swapCells(grid, r1, c1, r2, c2); // Use imported function
        } else {
          i--; // Retry if same cell picked
        }
      }
      // The loop will run again, re-applying ensureNoValidWords and ensureNoCorrectPositions
      // to the newly swapped grid before checking the validation state again.
    }
  } // End while loop

  if (!allYellowCheckPassed) {
    console.error(`Failed to achieve desired scramble state (with white tiles) after ${attemptCycle} attempts. The grid might start with all yellow tiles.`);
    // The grid will still be used, but it didn't meet the new preference.
  } else {
      console.log(`Scramble completed successfully in ${attemptCycle} cycle(s).`);
  }
};


/**
 * Ensure no valid words exist in the initial grid by breaking any found.
 * @param {Array<Array<string>>} grid - Grid to check and modify.
 * @param {Set<string>} validWordSet - Set of valid words.
 * @param {function(): number} randomFunc - Function to generate random numbers.
 */
export function ensureNoValidWords(grid, validWordSet, randomFunc) {
  let maxBreakAttempts = 100; // Limit attempts to break words
  let attempts = 0;
  const gridSize = grid.length;

  while (attempts < maxBreakAttempts) {
    let foundValidWord = false;

    // Check rows
    for (let row = 0; row < gridSize; row++) {
      const rowWord = getRowWord(grid, row); // Use imported function
      if (validWordSet.has(rowWord)) {
        // console.log(`Breaking valid row word: ${rowWord}`);
        breakWord(grid, row, true, randomFunc); // Use private helper
        foundValidWord = true;
        break; // Re-check all after a change
      }
    }

    if (foundValidWord) {
      attempts++;
      continue; // Restart checks from rows
    }

    // Check columns
    for (let col = 0; col < gridSize; col++) {
      const columnWord = getColumnWord(grid, col); // Use imported function
      if (validWordSet.has(columnWord)) {
        // console.log(`Breaking valid column word: ${columnWord}`);
        breakWord(grid, col, false, randomFunc); // Use private helper
        foundValidWord = true;
        break; // Re-check all after a change
      }
    }

    if (!foundValidWord) {
      // console.log("No valid words found in ensureNoValidWords.");
      return; // No valid words found, we are done
    }
    attempts++;
  }

  if (attempts >= maxBreakAttempts) {
    console.warn("ensureNoValidWords: Max attempts reached. Performing aggressive scramble.");
    // Fallback to a more aggressive scramble if targeted breaks fail
    aggressiveScramble(grid, randomFunc); // Use private helper
    // We might want to re-run ensureNoValidWords after aggressive scramble, but risk infinite loop.
    // For now, accept the result of the aggressive scramble.
  }
}


/**
 * Break a valid word by swapping two letters within the row or column.
 * @param {Array<Array<string>>} grid - Grid to modify.
 * @param {number} index - Row or column index to break.
 * @param {boolean} isRow - True if breaking a row word, false for column.
 * @param {function(): number} randomFunc - Function to generate random numbers.
 */
export function breakWord(grid, index, isRow, randomFunc) {
  const gridSize = grid.length;
  if (gridSize < 2) return; // Cannot swap in a 1x1 grid

  // Choose two distinct positions within the row/column
  let pos1 = Math.floor(randomFunc() * gridSize);
  let pos2;
  do {
    pos2 = Math.floor(randomFunc() * gridSize);
  } while (pos1 === pos2);

  if (isRow) {
    // Swap within the row
    swapCells(grid, index, pos1, index, pos2); // Use imported function
  } else {
    // Swap within the column
    swapCells(grid, pos1, index, pos2, index); // Use imported function
  }
}


/**
 * Perform a more aggressive scramble if normal methods fail.
 * Shuffles each row and then performs additional random swaps.
 * @param {Array<Array<string>>} grid - Grid to scramble.
 * @param {function(): number} randomFunc - Function to generate random numbers.
 */
export function aggressiveScramble(grid, randomFunc) {
  const gridSize = grid.length;

  // Shuffle each row independently first (introduces more chaos)
  for (let row = 0; row < gridSize; row++) {
    const shuffledRow = shuffleArray([...grid[row]], randomFunc); // Use imported function
    for (let col = 0; col < gridSize; col++) {
      grid[row][col] = shuffledRow[col];
    }
  }

  // Perform additional random swaps across the entire grid
  const numSwaps = gridSize * gridSize * 2; // More swaps
  for (let i = 0; i < numSwaps; i++) {
    const row1 = Math.floor(randomFunc() * gridSize);
    const col1 = Math.floor(randomFunc() * gridSize);
    const row2 = Math.floor(randomFunc() * gridSize);
    const col2 = Math.floor(randomFunc() * gridSize);
    if (row1 !== row2 || col1 !== col2) {
      swapCells(grid, row1, col1, row2, col2); // Use imported function
    } else {
      i--; // Retry
    }
  }
  console.log("Aggressive scramble performed.");
}


/**
 * Ensure no letters are in their correct final positions (no green tiles).
 * @param {Array<Array<string>>} grid - Grid to modify.
 * @param {Array<Array<string>>} solvedGrid - Solution grid for comparison.
 * @param {function(): number} randomFunc - Function to generate random numbers.
 */
export function ensureNoCorrectPositions(grid, solvedGrid, randomFunc) {
  const gridSize = grid.length;
  let maxMainAttempts = 50; // Attempts for targeted swaps
  let maxFinalAttempts = 100; // Attempts for random swaps if needed

  while (maxMainAttempts > 0) {
    // Find all positions where the letter is currently correct
    const correctPositions = [];
    for (let r = 0; r < gridSize; r++) {
      for (let c = 0; c < gridSize; c++) {
        if (grid[r][c] === solvedGrid[r][c]) {
          correctPositions.push({ row: r, col: c });
        }
      }
    }

    if (correctPositions.length === 0) {
      // console.log("No correct positions found in ensureNoCorrectPositions.");
      return; // Done
    }

    // Try to swap each correct letter with a random different position
    // Shuffle the positions to avoid potential biases
    const shuffledCorrectPositions = shuffleArray(correctPositions, randomFunc); // Use imported function

    for (const pos of shuffledCorrectPositions) {
       // Re-check if the position is still correct, it might have been swapped already
       if (grid[pos.row][pos.col] !== solvedGrid[pos.row][pos.col]) {
           continue;
       }

      let swapped = false;
      let findSwapAttempts = 20; // Attempts to find a suitable swap partner

      while (!swapped && findSwapAttempts > 0) {
        findSwapAttempts--;
        const r2 = Math.floor(randomFunc() * gridSize);
        const c2 = Math.floor(randomFunc() * gridSize);

        // Check if the swap is valid and desirable:
        // 1. Not swapping with itself
        // 2. The swap partner is NOT already correct
        // 3. The swap wouldn't make the partner position correct
        // 4. The swap wouldn't make the original position correct with the partner's letter
        if (
          (pos.row !== r2 || pos.col !== c2) &&                             // Not same cell
          grid[r2][c2] !== solvedGrid[r2][c2] &&                             // Partner not already green
          grid[pos.row][pos.col] !== solvedGrid[r2][c2] &&                   // Moving correct letter won't make partner green
          grid[r2][c2] !== solvedGrid[pos.row][pos.col]                      // Moving partner letter won't make original green
        ) {
          swapCells(grid, pos.row, pos.col, r2, c2); // Use imported function
          swapped = true;
        }
      }
       // If we couldn't find a suitable partner after several attempts for this one position,
       // we might just move on and hope the loop corrects it later.
    }

    maxMainAttempts--;
  }

  // If after targeted swaps, there are still correct positions, resort to random swaps
  if (hasCorrectPositions(grid, solvedGrid)) { // Use imported function
    console.warn("ensureNoCorrectPositions: Still correct positions after targeted swaps. Performing random swaps.");
    while (maxFinalAttempts > 0 && hasCorrectPositions(grid, solvedGrid)) { // Use imported function
      const r1 = Math.floor(randomFunc() * gridSize);
      const c1 = Math.floor(randomFunc() * gridSize);
      const r2 = Math.floor(randomFunc() * gridSize);
      const c2 = Math.floor(randomFunc() * gridSize);

      // Only perform swap if at least one of the positions is currently correct
      // and the swap doesn't create new correct positions.
      const pos1IsCorrect = grid[r1][c1] === solvedGrid[r1][c1];
      const pos2IsCorrect = grid[r2][c2] === solvedGrid[r2][c2];

      if ((pos1IsCorrect || pos2IsCorrect) && // At least one is green
          (r1 !== r2 || c1 !== c2) &&         // Not the same cell
          grid[r1][c1] !== solvedGrid[r2][c2] && // Swap won't make pos2 green
          grid[r2][c2] !== solvedGrid[r1][c1]    // Swap won't make pos1 green
         )
      {
        swapCells(grid, r1, c1, r2, c2); // Use imported function
      }
      maxFinalAttempts--;
    }
     if (hasCorrectPositions(grid, solvedGrid)) { // Use imported function
         console.error("ensureNoCorrectPositions: Failed to remove all correct positions even after random swaps.");
     }
  }
}


/**
 * Fix letter balance issues by comparing current grid to solution grid
 * and replacing excess letters with missing ones.
 * @param {Array<Array<string>>} grid - Grid to modify.
 * @param {Array<Array<string>>} solvedGrid - Solution grid for reference.
 */
export function fixLetterBalance(grid, solvedGrid) {
  const solutionCounts = countGridLetters(solvedGrid); // Use imported function
  const currentCounts = countGridLetters(grid); // Use imported function
  const gridSize = grid.length;

  const missing = [];
  const excess = [];

  // Find missing letters
  for (const [letter, expectedCount] of solutionCounts.entries()) {
    const currentCount = currentCounts.get(letter) || 0;
    for (let i = 0; i < expectedCount - currentCount; i++) {
      missing.push(letter);
    }
  }

  // Find excess letters and their positions
  const excessPositions = [];
  for (const [letter, currentCount] of currentCounts.entries()) {
    const expectedCount = solutionCounts.get(letter) || 0;
    for (let i = 0; i < currentCount - expectedCount; i++) {
      excess.push(letter);
    }
  }

  // If balance is already correct, return
  if (missing.length === 0 && excess.length === 0) {
      // console.log("fixLetterBalance: Balance is already correct.");
      return;
  }

   if (missing.length !== excess.length) {
       console.error(`CRITICAL: Mismatch in missing (${missing.length}) and excess (${excess.length}) letters during fixLetterBalance. Grid might be corrupted. Missing: ${missing.join('')}, Excess: ${excess.join('')}`);
       // Cannot reliably fix, maybe log grid states?
       console.log("Current Grid:", JSON.stringify(grid));
       console.log("Solution Grid:", JSON.stringify(solvedGrid));
       return; // Abort fix
   }


  // Find the actual grid positions of the excess letters
  const locatedExcess = [];
   outerLoop:
  for (let r = 0; r < gridSize; r++) {
    for (let c = 0; c < gridSize; c++) {
      const currentLetter = grid[r][c];
      const indexInExcess = excess.indexOf(currentLetter);
      if (indexInExcess !== -1) {
        // Found an excess letter at (r, c)
        locatedExcess.push({ row: r, col: c, letter: currentLetter });
        // Remove this instance from the list of needed excess letters
        excess.splice(indexInExcess, 1);
        if (locatedExcess.length === missing.length) {
             break outerLoop; // Found enough positions
        }
      }
    }
  }

  if (locatedExcess.length !== missing.length) {
       console.error(`CRITICAL: Could not locate all excess letter positions (${locatedExcess.length} found, ${missing.length} needed). Aborting fix.`);
       return;
   }


  // Shuffle missing letters to randomize replacement
  const shuffledMissing = shuffleArray(missing, Math.random); // Use imported function, standard random for this utility fix

  // Replace the excess letters with the missing ones
  for (let i = 0; i < locatedExcess.length; i++) {
    const pos = locatedExcess[i];
    const replacementLetter = shuffledMissing[i];
    // console.log(`Fixing balance: Replacing ${grid[pos.row][pos.col]} at (${pos.row},${pos.col}) with ${replacementLetter}`);
    grid[pos.row][pos.col] = replacementLetter;
  }

  console.log("Letter balance fixed.");
}

// Removed note about SqudleUtils.countGridLetters
